import { Link } from "react-router-dom";
import {
  Sparkle, MagnifyingGlass, FileText, Scales, UsersThree, Calculator, Question,
  ChartBar, Lightning, Buildings, ShieldCheck, ArrowRight, Globe, Database
} from "@phosphor-icons/react";
import { auth } from "../lib/api";

const FEATURES = [
  { icon: MagnifyingGlass, title: "Scouting Multi-Portale", desc: "Aggregazione da 10+ portali: CONSIP, MePA, MIT, ANAC, TED, portali regionali. Polling automatico ogni 4h." },
  { icon: Sparkle, title: "Analisi AI Documentale", desc: "Pipeline RAG su disciplinari: executive summary, requisiti strutturati, griglia OEPV, identificazione incumbent." },
  { icon: Scales, title: "Workflow Bid / No-Bid", desc: "Form 8 dimensioni, voto multi-decisore, motivazioni tracciate, lessons learned ISO 9001 / 231 ready." },
  { icon: FileText, title: "Relazione Tecnica AI", desc: "13 sezioni canoniche con generazione AI, tono configurabile (istituzionale/tecnico/consultivo), export PDF + DOCX." },
  { icon: UsersThree, title: "ATI / RTI Manager", desc: "Wizard raggruppamento, validazione quote 100%, calcolo requisiti cumulati, ruoli mandataria/mandanti." },
  { icon: Calculator, title: "Simulatore Economico", desc: "Monte Carlo (logistica) per win probability, expected value, alert anomalia art. 110 D.Lgs. 36/2023." },
  { icon: Question, title: "Smart Clarifications AI", desc: "Identificazione ambiguita nei disciplinari, generazione domande strategiche formali per la SA." },
  { icon: ChartBar, title: "Dashboard & Analytics", desc: "KPI operativi, breakdown per portale, trend scadenze, anomalie prezzi su CPV, AI insights azionabili." },
  { icon: Database, title: "RAG Chunking Semantico", desc: "Indicizzazione del disciplinare in chunks, Q&A su sezioni specifiche con citazioni e ranking." },
  { icon: Buildings, title: "Admin Portali", desc: "Gestione credenziali cifrate AES (Fernet), healthcheck, profili di ricerca per CPV/regione/importo." },
  { icon: Globe, title: "Gare Europee TED", desc: "Supporto bandi GUUE in eForms, generazione offerta multilingua, partner matchmaking UE (in roadmap)." },
  { icon: ShieldCheck, title: "Conformita & Sicurezza", desc: "GDPR ready, audit log completo, multi-tenancy con isolamento, cifratura credenziali, JWT + MFA." },
];

const HOW_IT_WORKS = [
  { step: "01", title: "Configura portali", desc: "Inserisci le credenziali dei portali pubblici (CONSIP, MePA, regionali, TED). Cifrate at-rest." },
  { step: "02", title: "Importa & Analizza", desc: "Le gare vengono intercettate o caricate manualmente. L'AI analizza il disciplinare e produce summary, requisiti, griglia." },
  { step: "03", title: "Decidi con il team", desc: "Workflow bid/no-bid strutturato. Vota con motivazione. Costruisci ATI se necessario." },
  { step: "04", title: "Redigi & Sottometti", desc: "L'AI genera le sezioni della relazione tecnica. Simula l'offerta economica. Esporta PDF/DOCX firmati." },
];

export default function Landing() {
  const loggedIn = !!auth.getToken();

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-zinc-200 bg-white sticky top-0 z-20" data-testid="landing-header">
        <div className="max-w-7xl mx-auto px-8 py-4 flex items-center justify-between">
          <Link to="/welcome" className="flex items-center gap-2" data-testid="landing-logo">
            <Sparkle weight="fill" className="text-klein" size={26} />
            <span className="font-display font-extrabold text-xl tracking-tight">GARE MANAGER AI</span>
          </Link>
          <nav className="flex items-center gap-2">
            <a href="#funzionalita" className="btn-ghost hidden md:inline-block">Funzionalita</a>
            <a href="#come-funziona" className="btn-ghost hidden md:inline-block">Come funziona</a>
            <Link to="/faq" className="btn-ghost hidden md:inline-block" data-testid="landing-faq-link">FAQ</Link>
            {loggedIn ? (
              <Link to="/dashboard" className="btn-primary" data-testid="landing-go-app">Vai alla Dashboard <ArrowRight size={14} className="inline ml-1" /></Link>
            ) : (
              <>
                <Link to="/login" className="btn-secondary" data-testid="landing-login">Accedi</Link>
                <Link to="/register" className="btn-primary" data-testid="landing-register">Inizia <ArrowRight size={14} className="inline ml-1" /></Link>
              </>
            )}
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-zinc-950 text-white">
        <div className="max-w-7xl mx-auto px-8 py-24 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <div className="label-up text-klein mb-4">Piattaforma AI / D.Lgs. 36/2023</div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-extrabold leading-[0.95] tracking-tighter mb-6" data-testid="hero-title">
              Vincere<br />
              gare pubbliche.<br />
              <span className="text-klein">Con intelligenza.</span>
            </h1>
            <p className="text-lg text-zinc-300 max-w-xl leading-relaxed mb-8">
              Scouting automatico multi-portale, analisi semantica AI di disciplinari e capitolati,
              identificazione incumbent, workflow bid/no-bid strutturato, redazione assistita della
              relazione tecnica e simulazione economica Monte Carlo. Tutto in un'unica piattaforma.
            </p>
            <div className="flex gap-3 flex-wrap">
              {loggedIn ? (
                <Link to="/dashboard" className="btn-primary" data-testid="hero-cta-app">Vai alla Dashboard</Link>
              ) : (
                <>
                  <Link to="/register" className="btn-primary" data-testid="hero-cta-start">Inizia gratis</Link>
                  <Link to="/login" className="btn-secondary bg-transparent border-white text-white hover:bg-white/10 hover:text-white" data-testid="hero-cta-login">Accedi</Link>
                </>
              )}
            </div>
            <div className="grid grid-cols-3 gap-6 mt-12 max-w-lg">
              <Metric label="Portali integrati" value="10+" />
              <Metric label="Moduli AI" value="22" />
              <Metric label="Codice rif." value="36/23" />
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="border border-zinc-800 bg-zinc-900 p-1 rounded-sm">
              <div className="bg-zinc-950 p-6 rounded-sm">
                <div className="label-up text-klein mb-3">Console AI</div>
                <div className="font-mono text-xs space-y-1 text-zinc-300">
                  <div>$ analyze --tender CIG-A1B2C3D4</div>
                  <div className="text-emerald-400">→ Indexing 24 chunks (pgvector compatible)</div>
                  <div className="text-emerald-400">→ Extracting 12 requirements...</div>
                  <div className="text-emerald-400">→ Building OEPV grid...</div>
                  <div className="text-amber-400">⚠ Incumbent detected: Engineering S.p.A. (score 78/100)</div>
                  <div className="text-emerald-400">→ Feasibility: CONTENDIBILE (54/100)</div>
                  <div>$ generate --section "Approccio Metodologico" --tone tecnico</div>
                  <div className="text-violet-400">✦ AI drafted: 847 words, 100% coverage</div>
                  <div>$ simulate --bid 384000 --competitors 5</div>
                  <div className="text-emerald-400">→ Win probability: 68.3% | Margin P50: EUR 124k</div>
                  <div>$ export --format pdf --output relazione.pdf</div>
                  <div className="text-zinc-400">→ Generated 32 pages with TOC.</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="funzionalita" className="py-24 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-8">
          <div className="mb-12 max-w-2xl">
            <div className="label-up mb-3">Funzionalita</div>
            <h2 className="font-display text-4xl font-extrabold tracking-tighter mb-3">
              Il ciclo di vita completo della partecipazione a gare.
            </h2>
            <p className="text-zinc-600">Dalla pubblicazione del bando alla firma del contratto, 22 moduli specializzati guidano ogni decisione.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-200" data-testid="features-grid">
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} className="bg-white p-6 hover:bg-zinc-50 transition-colors" data-testid={`feature-${i}`}>
                  <div className="flex items-start justify-between mb-4">
                    <Icon size={28} weight="duotone" className="text-klein" />
                    <span className="font-mono text-[10px] text-zinc-400">{String(i + 1).padStart(2, "0")}</span>
                  </div>
                  <h3 className="font-display font-bold text-lg tracking-tight mb-2">{f.title}</h3>
                  <p className="text-sm text-zinc-600 leading-relaxed">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="come-funziona" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-8">
          <div className="mb-12 max-w-2xl">
            <div className="label-up mb-3">Come funziona</div>
            <h2 className="font-display text-4xl font-extrabold tracking-tighter">Quattro passi. Una vittoria.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-zinc-200">
            {HOW_IT_WORKS.map((s, i) => (
              <div key={i} className="bg-white p-6">
                <div className="font-display font-extrabold text-5xl text-klein tracking-tighter">{s.step}</div>
                <h3 className="font-display font-bold text-xl mt-4 mb-2 tracking-tight">{s.title}</h3>
                <p className="text-sm text-zinc-600 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Compliance band */}
      <section className="py-16 bg-zinc-950 text-white">
        <div className="max-w-7xl mx-auto px-8 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="label-up text-klein mb-3">Compliance &amp; Sicurezza</div>
            <h2 className="font-display text-3xl font-extrabold tracking-tighter mb-4">
              Conforme al Nuovo Codice dei Contratti Pubblici.
            </h2>
            <p className="text-zinc-400 leading-relaxed">
              Tracciatura completa delle decisioni, audit log, cifratura at-rest delle credenziali portali,
              multi-tenancy con isolamento, JWT + MFA. Pronto per ISO 9001, modelli 231, GDPR.
              Architettura LLM-agnostic con LiteLLM gateway.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-px bg-zinc-800">
            {["D.Lgs. 36/2023", "GDPR", "ISO 9001", "ISO 27001", "NIS2", "EAA / WCAG 2.1 AA", "AGID", "PNRR-ready"].map((c, i) => (
              <div key={i} className="bg-zinc-950 p-4 text-center">
                <div className="font-display font-bold text-sm">{c}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-white">
        <div className="max-w-3xl mx-auto px-8 text-center">
          <Lightning weight="fill" className="text-klein mx-auto mb-4" size={32} />
          <h2 className="font-display text-4xl font-extrabold tracking-tighter mb-4">Pronto a partecipare?</h2>
          <p className="text-zinc-600 mb-8">
            Crea un account, importa la prima gara o popola con dati demo. L'AI fa il resto.
          </p>
          {loggedIn ? (
            <Link to="/dashboard" className="btn-primary" data-testid="cta-dashboard">Vai alla Dashboard</Link>
          ) : (
            <Link to="/register" className="btn-primary" data-testid="cta-register">Crea account gratuito</Link>
          )}
        </div>
      </section>

      <footer className="border-t border-zinc-200 py-8 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-8 flex flex-wrap justify-between gap-4 text-xs text-zinc-500">
          <div>© 2026 Like Digital S.r.l. — Gare Manager AI v1.0</div>
          <div className="flex gap-4 font-mono">
            <Link to="/faq">FAQ</Link>
            <span>GDPR</span>
            <span>D.Lgs. 36/2023</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="border-l border-zinc-800 pl-4">
      <div className="font-display font-extrabold text-3xl tracking-tighter">{value}</div>
      <div className="label-up text-zinc-500">{label}</div>
    </div>
  );
}
